import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Stack, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Modal } from "@4uhub/lib4uhub";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import Loading from "../../../../components/Loading";
import RadioGroupInputIcon from "../../../../components/UI/Inputs/RadioGroupInputIcon";
import useFetch from "../../../../hooks/useFetch";
import InsuranceForm from "../../Insurance/AddInsurance/InsuranceForm";
import { ScheduleInsuranceService } from "../../Insurance/insurance.service";
import { IInsuranceFavorite } from "../../Insurance/model";
import { TAppointment } from "../schema";

const Insurance = () => {
  const [insurances, setInsurances] = useState<IInsuranceFavorite[]>([]);

  const [open, setOpen] = useState(false);

  const { getValues, setValue } = useFormContext<TAppointment>();

  const { t } = useTranslation();

  const { sendRequest, loading } = useFetch(ScheduleInsuranceService.list);

  const { sendRequest: onFavorite, loading: fLoading } = useFetch(
    ScheduleInsuranceService.editMain
  );

  const fetch = useCallback(async () => {
    const { patient, insurance } = getValues();

    if (!patient.individualId) return;

    const { data, success } = await sendRequest({
      IndividualId: patient.individualId,
    });

    if (data && success) {
      if (!insurance) {
        const favorite = data.find((i) => i.main);
        if (favorite) {
          setValue("insurance", {
            id: favorite.code,
            name: favorite.insuranceName,
            code: favorite.code,
          });
        }
      }
      setInsurances(data);
    }
  }, [sendRequest, getValues, setValue]);

  const onClickHandler = () => {
    setOpen(true);
  };

  const onClickStarHandler = useCallback(
    async (selectedInsurance: IInsuranceFavorite) => {
      if (!selectedInsurance) return;

      const { data, success } = await onFavorite({
        insuranceIntegrationId: selectedInsurance.insuranceIntegrationId,
        data: {
          insuranceIntegrationId: selectedInsurance.insuranceIntegrationId,
        },
      });

      if (data && success) {
        fetch();
      }
    },
    [onFavorite, fetch]
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleSuccess = () => {
    handleClose();
    fetch();
  };

  useEffect(() => {
    fetch();
  }, [fetch]);

  if (loading || fLoading) {
    return <Loading />;
  }

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={`${t("page.schedules.newSchedule.insurance")}`}
        maxDialogWidth="xs"
      >
        <InsuranceForm onSuccess={handleSuccess} />
      </Modal>
      <Stack gap={1}>
        <RadioGroupInputIcon
          name="insurance"
          groupLabel={t("page.schedules.newSchedule.insurance")}
          valueField="code"
          disableBorder
          radios={insurances.map((i) => ({
            label: i.insuranceName,
            value: {
              id: i.code,
              name: i.insuranceName,
              code: i.code,
              insuranceName: i.insuranceName,
              main: i.main,
              insuranceIntegrationId: i.insuranceIntegrationId,
              individual: i.individual,
              individualId: i.individualId,
            },
          }))}
          icon={<StarRateRoundedIcon />}
          onIconClick={onClickStarHandler}
        />
        <Stack
          alignItems={"center"}
          direction={"row"}
          onClick={onClickHandler}
          gap={1}
          sx={{
            cursor: "pointer",
            "&:hover": {
              opacity: 0.7,
            },
          }}
        >
          <AddCircleOutlineIcon color="inherit" fontSize="small" />
          <Typography>
            {t("page.schedules.newSchedule.addInsurance")}
          </Typography>
        </Stack>
      </Stack>
    </>
  );
};

export default memo(Insurance);
